import { useEffect, useRef, type KeyboardEvent } from "react";

export type ChatLang = "hr" | "en";

export interface ChatSearchBarProps {
  query: string;
  onQuery: (value: string) => void;
  count: number;
  active: number;
  onPrev: () => void;
  onNext: () => void;
  onClose: () => void;
  lang: ChatLang;
}

function MagnifyIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-4 w-4 shrink-0"
    >
      <circle cx="9" cy="9" r="5.5" />
      <line x1="13.2" y1="13.2" x2="17" y2="17" />
    </svg>
  );
}

function ChevronUpIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-4 w-4 shrink-0"
    >
      <polyline points="5 12.5 10 7.5 15 12.5" />
    </svg>
  );
}

function ChevronDownIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-4 w-4 shrink-0"
    >
      <polyline points="5 7.5 10 12.5 15 7.5" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-4 w-4 shrink-0"
    >
      <line x1="5" y1="5" x2="15" y2="15" />
      <line x1="15" y1="5" x2="5" y2="15" />
    </svg>
  );
}

const iconButtonClass =
  "inline-flex min-h-11 min-w-11 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

export function ChatSearchBar({
  query,
  onQuery,
  count,
  active,
  onPrev,
  onNext,
  onClose,
  lang,
}: ChatSearchBarProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const t = {
    label: lang === "en" ? "Search the conversation" : "Traži u razgovoru",
    placeholder: lang === "en" ? "Search the conversation…" : "Traži u razgovoru…",
    prev: lang === "en" ? "Previous match" : "Prethodni pogodak",
    next: lang === "en" ? "Next match" : "Sljedeći pogodak",
    close: lang === "en" ? "Close search" : "Zatvori pretragu",
    noMatches: lang === "en" ? "No matches" : "Nema pogodaka",
  };

  let counter = "";
  if (count > 0) {
    counter = `${active + 1} / ${count}`;
  } else if (query.trim().length >= 2) {
    counter = t.noMatches;
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Enter") {
      if (count > 0) {
        event.preventDefault();
        if (event.shiftKey) {
          onPrev();
        } else {
          onNext();
        }
      }
      return;
    }
    if (event.key === "Escape") {
      onClose();
    }
  }

  return (
    <div
      data-testid="chat-search"
      role="search"
      className="flex shrink-0 items-center gap-1 border-b border-border bg-background px-3 py-1"
    >
      <span className="flex min-w-0 shrink-0 items-center text-muted-foreground">
        <MagnifyIcon />
      </span>
      <input
        ref={inputRef}
        data-testid="chat-search-input"
        type="search"
        aria-label={t.label}
        placeholder={t.placeholder}
        value={query}
        maxLength={200}
        onChange={(e) => onQuery(e.target.value)}
        onKeyDown={handleKeyDown}
        className="min-w-0 flex-1 bg-transparent px-1 py-2 text-sm text-foreground outline-none placeholder:text-muted-foreground"
      />
      <span
        data-testid="chat-search-count"
        aria-live="polite"
        className="shrink-0 whitespace-nowrap px-1 text-xs tabular-nums text-muted-foreground"
      >
        {counter}
      </span>
      <button
        type="button"
        data-testid="chat-search-prev"
        aria-label={t.prev}
        title={t.prev}
        onClick={onPrev}
        disabled={count === 0}
        className={iconButtonClass}
      >
        <ChevronUpIcon />
      </button>
      <button
        type="button"
        data-testid="chat-search-next"
        aria-label={t.next}
        title={t.next}
        onClick={onNext}
        disabled={count === 0}
        className={iconButtonClass}
      >
        <ChevronDownIcon />
      </button>
      <button
        type="button"
        data-testid="chat-search-close"
        aria-label={t.close}
        title={t.close}
        onClick={onClose}
        className={iconButtonClass}
      >
        <CloseIcon />
      </button>
    </div>
  );
}

export default ChatSearchBar;
